<script setup>
import { ref } from 'vue'
import { usePublic } from '@/stores/public'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  email: {
    type: String,
  },
  customerId: {
    type: String,
  },
  attributes: Object,
  preview: {
    type: Boolean,
    default: false,
  },
})

const storePublic = usePublic()

const oldEmail = ref(props.email)
const newEmail = ref('')
const alertMsg = ref('')
const { t } = useI18n()
const emit = defineEmits(['save', 'close'])

async function save(e) {
  e.preventDefault()
  if (!newEmail.value) {
    showAlert(t('modal_mail.error_empty'))
  } else if (newEmail.value === oldEmail.value) {
    showAlert(t('modal_mail.error_same'))
  } else {
    if (props.preview) {
      emit('save', newEmail.value)
      return
    }
    if (newEmail.value != oldEmail.value) {
      try {
        await storePublic.updateCustomerEmail({
          id: props.customerId,
          email: newEmail.value,
        })
        window.localStorage.setItem('email', newEmail.value)
        emit('save', newEmail.value)
      } catch (error) {
        showAlert(t('modal_mail.error_exists'))
      }
    }
  }
}

function close(e) {
  e.preventDefault()
  emit('close')
}

function showAlert(msg) {
  alertMsg.value = msg
}
</script>

<template>
  <div class="px-4">
    <div class="border w-fit h-fit p-2 rounded-lg">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
        />
      </svg>
    </div>

    <div class="mt-5">
      <div class="text-start">
        <header class="pb-4">
          <h1 class="text-xl font-medium mb-2">{{ $t('modal_mail.main_title') }}</h1>
          <p>{{ $t('modal_mail.msg', { email: props.email }) }}</p>
        </header>

        <div>
          <div class="mb-6 flex flex-col">
            <label class="text-lg font-medium mb-2">{{ $t('modal_mail.input_title') }}</label>
            <input v-model="newEmail" type="text" name="listName" class="border rounded px-4 py-2 w-full" :placeholder="$t('modal_mail.input_placeholder')" />
            <span v-if="alertMsg" class="error-text">
              {{ alertMsg }}
            </span>
          </div>

          <div class="flex text-right mt-2 justify-between">
            <button
              type="button"
              class="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 w-[45%] max-h-10"
              @click="close"
            >
              {{ $t('modal_mail.cancel_btn') }}
            </button>
            <button
              type="button"
              class="rounded-md bg-[#0099D0] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-[45%] max-h-10"
              :style="{ backgroundColor: attributes?.primary_color || null }"
              @click="save"
            >
              {{ $t('modal_mail.update_btn') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.error-text {
  color: red;
  font-size: xx-small;
  margin-top: 0.5rem;
}
</style>
